import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Alert, Modal } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";

function normalizeSelectOptions(options, valueKeys = [], labelKeys = []) {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((item) => {
      if (!item || typeof item !== "object") {
        const fallback = item == null ? "" : String(item);
        return { value: fallback, label: fallback };
      }

      if (Object.prototype.hasOwnProperty.call(item, "value") && Object.prototype.hasOwnProperty.call(item, "label")) {
        return { value: item.value, label: item.label };
      }

      const valueFromKeys = valueKeys.map((key) => item[key]).find((val) => val !== undefined && val !== null && val !== "");
      const labelFromKeys = labelKeys.map((key) => item[key]).find((val) => val !== undefined && val !== null && val !== "");
      const firstValue = Object.values(item)[0];
      const value = valueFromKeys ?? firstValue ?? "";
      const label = labelFromKeys ?? value;

      return { value, label };
    })
    .filter((opt) => opt.value !== undefined && opt.value !== null);
}

function LeaseReviewList({
  isAuthenticated,
  user,
  filter,
  leaseId,
  data,
  updateTableState,
  saveRecord,
  deleteRecord,
  leaseTermOptions,
  reviewTypeOptions,
  validateRelatedRecord,
}) {
  const dataSet = "leaseReviews";
  const dataSetPrimaryKey = "lease_review_id";
  const createId = useMemo(() => createElementIdFactory("lease-review-list"), []);

  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState(data || []);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [cachedReviewTypeOptions, setCachedReviewTypeOptions] = useState([]);

  const leaseTermSelectOptions = useMemo(
    () => normalizeSelectOptions(leaseTermOptions, ["value", "lease_term", "code"], ["label", "lease_term", "description"]),
    [leaseTermOptions]
  );

  const reviewTypeSelectOptionsFromLookup = useMemo(
    () => normalizeSelectOptions(reviewTypeOptions, ["value", "review_type_code", "code"], ["label", "review_type", "description"]),
    [reviewTypeOptions]
  );

  useEffect(() => {
    if (reviewTypeSelectOptionsFromLookup.length > 0) {
      setCachedReviewTypeOptions(reviewTypeSelectOptionsFromLookup);
    }
  }, [reviewTypeSelectOptionsFromLookup]);

  const reviewTypeSelectOptionsFromData = useMemo(() => {
    const source = Array.isArray(tableData) ? tableData : [];
    return source
      .filter((row) => row && row.review_type_code != null && String(row.review_type_code).trim() !== "")
      .map((row) => {
        const value = String(row.review_type_code).trim();
        const label = row.review_type != null && String(row.review_type).trim() !== "" ? String(row.review_type) : value;
        return { value, label };
      });
  }, [tableData]);

  const reviewTypeSelectOptions = useMemo(() => {
    const preferredSource = reviewTypeSelectOptionsFromLookup.length > 0 ? reviewTypeSelectOptionsFromLookup : cachedReviewTypeOptions;
    const combined = [...preferredSource, ...reviewTypeSelectOptionsFromData];
    const map = new Map();

    combined.forEach((opt) => {
      if (!opt || opt.value == null) {
        return;
      }

      const key = String(opt.value);
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: opt.label != null && String(opt.label).trim() !== "" ? String(opt.label) : key,
        });
      }
    });

    return Array.from(map.values());
  }, [cachedReviewTypeOptions, reviewTypeSelectOptionsFromData, reviewTypeSelectOptionsFromLookup]);

  useEffect(() => {
    setTableData(data || []);
  }, [data]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(false);
  }, [isAuthenticated]);

  const notifyParent = (nextData, rowIndex, columnId, value) => {
    if (typeof updateTableState !== "function") {
      return;
    }

    if (updateTableState.length >= 3) {
      updateTableState(rowIndex, columnId, value);
      return;
    }

    updateTableState(nextData, dataSet);
  };

  const sanitizeCellValue = (columnId, value) => {
    if (columnId === "review_type_code" || columnId === "lease_term") {
      return value == null ? "" : String(value).trim();
    }

    return value;
  };

  const updateLocalCell = (rowIndex, columnId, value) => {
    const normalizedValue = sanitizeCellValue(columnId, value);

    setTableData((prev) => {
      const next = prev.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        const nextRow = {
          ...row,
          [columnId]: normalizedValue,
          modified: true,
        };

        if (columnId === "review_type_code") {
          const selected = reviewTypeSelectOptions.find((opt) => String(opt.value) === String(normalizedValue));
          nextRow.review_type = selected ? String(selected.label) : row.review_type || "";
        }

        return nextRow;
      });

      notifyParent(next, rowIndex, columnId, normalizedValue);
      return next;
    });
  };

  const handleDataSave = async (record) => {
    if (typeof saveRecord !== "function") {
      return null;
    }

    // Resolve lease_id: prefer the record's own value, then the explicit leaseId prop,
    // then fall back to filter.leaseid (only set in constructor, not after reset).
    const resolvedLeaseId = record.lease_id ?? leaseId ?? filter?.leaseid ?? null;

    const recordWithLease = {
      ...record,
      lease_id: resolvedLeaseId,
    };

    return saveRecord.length >= 3
      ? await saveRecord(recordWithLease, dataSet, dataSetPrimaryKey)
      : await saveRecord(dataSet, recordWithLease);
  };

  const handleSave = async ({ row, column, value }) => {
    const rowIndex = row?.index ?? -1;
    const columnId = column?.id;
    const normalizedValue = columnId ? sanitizeCellValue(columnId, value) : value;

    const nextData = [...(tableData || [])];

    if (rowIndex >= 0 && rowIndex < nextData.length && columnId) {
      const current = nextData[rowIndex] || {};
      const updated = {
        ...current,
        [columnId]: normalizedValue,
        modified: true,
      };

      if (columnId === "review_type_code") {
        const selected = reviewTypeSelectOptions.find((opt) => String(opt.value) === String(normalizedValue));
        updated.review_type = selected ? String(selected.label) : current.review_type || "";
      }

      nextData[rowIndex] = updated;
    }

    const currencyFormatter = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      currencyDisplay: "symbol",
    });

    let savingNewRecord = false;

    for (let i = 0; i < nextData.length; i += 1) {
      const record = { ...(nextData[i] || {}) };

      if (typeof validateRelatedRecord === "function") {
        const validation = validateRelatedRecord(record, dataSet);
        if (Array.isArray(validation) && validation[0] === false) {
          setErrorMessage(validation[1] || "Validation failed.");
          if (record.new_record) {
            setShowWarning(true);
          } else {
            setShowAlert(true);
          }
          return false;
        }
      }

      if (record.modified !== true) {
        continue;
      }

      if (record.revised_price_per_sqm) {
        record.revised_price_per_sqm = currencyFormatter.format(String(record.revised_price_per_sqm).replace(/[^0-9.]/g, ""));
      }

      if (record.revised_price_per_annum) {
        record.revised_price_per_annum = currencyFormatter.format(String(record.revised_price_per_annum).replace(/[^0-9.]/g, ""));
      }

      if (!savingNewRecord) {
        if (record.new_record) {
          savingNewRecord = true;
        }

        try {
          const result = await handleDataSave(record);
          const saveFailed = result && result.result === false;

          if (saveFailed) {
            setErrorMessage(result.error_message || "Unable to save lease review record.");
            if (record.new_record) {
              setShowWarning(true);
            } else {
              setShowAlert(true);
            }
            return false;
          }

          record.modified = false;

          if (record.new_record) {
            if (result && result.id) {
              record[dataSetPrimaryKey] = result.id;
            }
            record.new_record = false;
            savingNewRecord = false;
          }

          nextData[i] = record;
        } catch (error) {
          setErrorMessage(error?.message || "Unexpected error saving lease review record.");
          setShowAlert(true);
          return false;
        }
      }
    }

    if (nextData.length > 0 && Object.prototype.hasOwnProperty.call(nextData[0], "sort_order")) {
      nextData.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }

    setShowAlert(false);
    setShowWarning(false);
    setErrorMessage("");
    setTableData(nextData);
    notifyParent(nextData, rowIndex, columnId || dataSetPrimaryKey, normalizedValue ?? null);
    return true;
  };

  const handleDelete = useCallback(async ({ row }) => {
    const record = row?.original;
    if (!record) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return;
    }

    try {
      // Match old class behavior: archive by setting archived_date and saving (PUT),
      // rather than calling a DELETE endpoint.
      if (!record.new_record) {
        const archivedRecord = {
          ...record,
          archived_date: new Date(),
        };
        await handleDataSave(archivedRecord);
      }

      setTableData((prev) => {
        const next = prev.filter((_, index) => index !== row.index);
        notifyParent(next, row.index, dataSetPrimaryKey, null);
        return next;
      });
    } catch (error) {
      setErrorMessage(error?.message || "Unexpected error deleting lease review record.");
      setShowAlert(true);
    }
  }, [dataSetPrimaryKey, handleDataSave, notifyParent]);

  const addRecord = () => {
    const records = [...(tableData || [])];
    const createdBy = typeof user === "string" ? user : user?.name || "";

    if (records.length > 0) {
      const lastRecord = records[records.length - 1];
      const dt = new Date(new Date(lastRecord.review_date).setFullYear(new Date(lastRecord.review_date).getFullYear() + 1));
      const nextReviewDate = Number.isNaN(dt.getTime()) ? new Date() : dt.toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });

      records.push({
        lease_id: leaseId ?? filter?.leaseid ?? null,
        review_date: nextReviewDate,
        lease_term: lastRecord.lease_term,
        review_type_code: lastRecord.review_type_code,
        review_type: lastRecord.review_type,
        percentage: lastRecord.percentage,
        revised_price_per_sqm: null,
        revised_price_per_annum: null,
        price_incl_gst: false,
        created_by: createdBy,
        last_edited_by: createdBy,
        new_record: true,
      });
    } else {
      records.push({
        lease_id: leaseId ?? filter?.leaseid ?? null,
        review_date: new Date(),
        revised_price_per_sqm: null,
        revised_price_per_annum: null,
        price_incl_gst: false,
        created_by: createdBy,
        last_edited_by: createdBy,
        new_record: true,
      });
    }

    setTableData(records);
    notifyParent(records, records.length - 1, dataSetPrimaryKey, null);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "lease_review_id",
        id: "lease_review_id",
        header: "ID",
        size: 100,
        cell: (info) => {
          const value = info.getValue();
          if (!value) {
            return null;
          }

          return (
            <LinkContainer to={`../leasereview/${value}`}>
              <a href={`../leasereview/${value}`}>{value}</a>
            </LinkContainer>
          );
        },
      },
      {
        accessorKey: "review_date",
        id: "review_date",
        header: "Review Date",
        cell: EditableCell,
        size: 120,
      },
      {
        accessorKey: "lease_term",
        id: "lease_term",
        header: "Lease Term",
        cell: EditableCell,
        meta: { type: "select", options: leaseTermSelectOptions },
        size: 250,
      },
      {
        accessorKey: "review_type_code",
        id: "review_type_code",
        header: "Review Type",
        cell: EditableCell,
        meta: { type: "select", options: reviewTypeSelectOptions },
        size: 250,
      },
      {
        accessorKey: "percentage",
        id: "percentage",
        header: "Percentage",
        cell: EditableCell,
        meta: { suffix: "%" },
        size: 150,
      },
      {
        accessorKey: "revised_price_per_sqm",
        id: "revised_price_per_sqm",
        header: "Revised Price per sqm",
        cell: EditableCell,
        size: 150,
      },
      {
        accessorKey: "revised_price_per_annum",
        id: "revised_price_per_annum",
        header: "Revised Price per annum",
        cell: EditableCell,
        size: 150,
      },
      {
        accessorKey: "price_incl_gst",
        id: "price_incl_gst",
        header: "Inc GST",
        cell: EditableCell,
        meta: { type: "checkbox" },
        size: 120,
      },
      {
        id: "delete_row",
        header: "",
        size: 100,
        enableSorting: false,
        cell: (info) => {
          const rowId = info.row.original?.lease_review_id ?? info.row.id;
          return (
            <Button
              id={createId(`delete-${rowId}`)}
              type="button"
              size="sm"
              variant="danger"
              onClick={() => handleDelete({ row: info.row })}
            >
              Delete
            </Button>
          );
        },
      },
    ],
    [createId, handleDelete, leaseTermSelectOptions, reviewTypeSelectOptions]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div id={createId("root")} className="LeaseReviewList">
      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">{errorMessage}</Alert>
        </Modal.Body>
      </Modal>

      {showWarning && <Alert id={createId("warning")} variant="danger">{errorMessage}</Alert>}

      <Row>
        <Col className="text-right">
          <Button id={createId("add-button")} variant="secondary" size="sm" onClick={addRecord}>
            Add
          </Button>
        </Col>
      </Row>

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          {isLoading ? (
            <div className="outer-div" id={createId("loading")}>
              <div className="sweet-loading inner-div">
                <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
              </div>
            </div>
          ) : (
            <GenericTable
              columns={columns}
              data={tableData || []}
              section="lease-review-list"
              pageSize={10}
              hasHeaderSorting
              meta={{
                updateData: updateLocalCell,
                handleSave,
                handleDelete,
              }}
              elementIds={{
                prefix: createId("table"),
                row: (row) => createId(`row-${row.id}`),
                cell: (cell) => createId(`cell-${cell.id}`),
              }}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

export default LeaseReviewList;
